import * as React from "react";
import { useState, useEffect } from "react";

import { withTypes } from "react-final-form";
import { Field } from "react-final-form";
import axios from "axios";

import {
  Button,
  CircularProgress,
  TextField,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import {
  Notification,
  useNotify,
} from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import CustomConfirm from 'ra-custom-confirm';
import Check from '@material-ui/icons/Check';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import SettingsIcon from '@material-ui/icons/Settings';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import PpsMasterAccounts from './PpsMasterAccounts';

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "3em 2em",
  },
  container: {
    width: "100%",
    maxWidth: "1100px",
  },
  pageTitle: {
    marginBottom: "1.5em",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  balanceGrid: {
    marginBottom: "2em",
  },
  balanceCard: {
    height: "100%",
    borderRadius: "16px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    },
  },
  rewardsBalanceCard: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
  },
  configCard: {
    background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    color: "white",
  },
  balanceCardContent: {
    padding: "2em !important",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  balanceIcon: {
    fontSize: "2.5rem",
    marginBottom: "0.5em",
    opacity: 0.9,
  },
  balanceLabel: {
    fontSize: "1rem",
    fontWeight: 500,
    opacity: 0.9,
    marginBottom: "0.5em",
  },
  balanceValue: {
    fontSize: "2rem",
    fontWeight: 700,
    marginBottom: "0.25em",
  },
  balanceSubtext: {
    fontSize: "0.85rem",
    opacity: 0.8,
  },
  refreshButton: {
    marginTop: "auto",
    alignSelf: "flex-start",
    color: "white",
    borderColor: "rgba(255,255,255,0.5)",
    "&:hover": {
      borderColor: "white",
      backgroundColor: "rgba(255,255,255,0.1)",
    },
  },
  transferGroup: {
    padding: "1.5em",
    backgroundColor: theme.palette.grey[50],
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  transferGroupTitle: {
    fontWeight: 600,
    marginBottom: "1em",
    display: "flex",
    alignItems: "center",
    gap: "0.5em",
  },
  formField: {
    marginBottom: "1em",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  button: {
    padding: "12px 24px",
    fontSize: "0.95rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    },
  },
  warningBox: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ffb74d",
    borderRadius: "8px",
    padding: "1em",
    marginBottom: "1em",
  },
  warningText: {
    color: "#e65100",
    fontSize: "0.9rem",
  },
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "3em",
  },
  tabsContainer: {
    marginBottom: "2em",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "1rem",
      minHeight: "60px",
    },
    "& .Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
  tabPanel: {
    padding: "2em",
  },
}));

interface FormValues {
  tillo_amount?: string;
  tillo_payment_reference?: string;
  natwest_rewards_amount?: string;
  natwest_rewards_payment_reference?: string;
  external_source_account_no?: string;
  external_cardholder_ref?: string;
  external_payee_name?: string;
  external_account_no?: string;
  external_sort_code?: string;
  external_amount?: string;
  external_payment_reference?: string;
}

interface ConfigData {
  rewards_account_no?: string;
  rewards_cardholder_ref?: string;
  tillo_float_payee_reference?: string;
  rewards_minimum_threshold?: number;
  tillo_balance_threshold?: number;
}

interface RewardsBalance {
  account_no: string;
  balance_pounds: number;
  minimum_threshold: number;
  available_for_transfer: number;
}

const { Form } = withTypes<FormValues>();

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  helperText,
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={(touched && error) || helperText}
    {...inputProps}
    {...props}
    fullWidth
    variant="outlined"
  />
);

const PpsMoneyManagement = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loadingRewards, setLoadingRewards] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [transferring, setTransferring] = useState(false);

  const [rewardsBalance, setRewardsBalance] = useState<RewardsBalance | null>(null);
  const [configData, setConfigData] = useState<ConfigData | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transferType, setTransferType] = useState<"tillo" | "natwest_rewards" | "external" | null>(null);
  const [transferData, setTransferData] = useState<any>(null);
  const [pendingFormValues, setPendingFormValues] = useState<FormValues | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = () => {
    fetchRewardsBalance();
    fetchConfig();
  };

  const fetchRewardsBalance = () => {
    setLoadingRewards(true);
    let config = getConfigGet({ action: "get_rewards_balance" });

    axios
      .get(ENDPOINTS.PPS_MONEY_MANAGEMENT, config)
      .then((res: any) => {
        setLoadingRewards(false);
        setRewardsBalance(res.data);
      })
      .catch((error) => {
        setLoadingRewards(false);
        notify(
          error?.response?.data?.message || "Failed to fetch rewards balance",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const fetchConfig = () => {
    setLoadingConfig(true);
    let config = getConfigGet({ action: "get_config" });

    axios
      .get(ENDPOINTS.PPS_MONEY_MANAGEMENT, config)
      .then((res: any) => {
        setLoadingConfig(false);
        setConfigData(res.data);
      })
      .catch((error) => {
        setLoadingConfig(false);
        notify(
          error?.response?.data?.message || "Failed to fetch configuration",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleTilloTransferClick = (values: FormValues) => {
    const amount = parseFloat(values.tillo_amount || "0");
    if (amount <= 0) {
      notify("Please enter a valid amount", "error", "", undefined, 5000);
      return;
    }

    if (rewardsBalance && amount > rewardsBalance.available_for_transfer) {
      notify(
        `Amount exceeds available balance for transfer (£${rewardsBalance.available_for_transfer.toFixed(2)})`,
        "error",
        "",
        undefined,
        5000
      );
      return;
    }

    setTransferType("tillo");
    setPendingFormValues(values);
    setTransferData({
      type: "Rewards to Tillo Float Transfer",
      amount: amount,
      payment_reference: values.tillo_payment_reference || "Auto-generated",
      rewards_balance: rewardsBalance?.balance_pounds,
      available_for_transfer: rewardsBalance?.available_for_transfer,
    });
    setConfirmOpen(true);
  };

  const handleNatwestRewardsTransferClick = (values: FormValues) => {
    const amount = parseFloat(values.natwest_rewards_amount || "0");
    if (amount <= 0) {
      notify("Please enter a valid amount", "error", "", undefined, 5000);
      return;
    }

    setTransferType("natwest_rewards");
    setPendingFormValues(values);
    setTransferData({
      type: "NatWest to Rewards Wallet Transfer",
      amount: amount,
      payment_reference: values.natwest_rewards_payment_reference || "Auto-generated",
    });
    setConfirmOpen(true);
  };

  const externalSource = (values: FormValues) => ({
    account_no: values.external_source_account_no || configData?.rewards_account_no || "",
    cardholder_ref: values.external_cardholder_ref || configData?.rewards_cardholder_ref || "",
  });

  const handleExternalTransferClick = (values: FormValues) => {
    const amount = parseFloat(values.external_amount || "0");
    const source = externalSource(values);
    const sortCode = (values.external_sort_code || "").replace(/[-\s]/g, "");

    if (!source.account_no || !source.cardholder_ref) {
      notify("Source account and cardholder reference are required", "error", "", undefined, 5000);
      return;
    }
    if (!/^[A-Za-z0-9 .&'-]{1,18}$/.test((values.external_payee_name || "").trim())) {
      notify("Payee name: up to 18 letters, digits, spaces or . & ' -", "error", "", undefined, 5000);
      return;
    }
    if (!/^\d{8}$/.test(values.external_account_no || "")) {
      notify("Account number must be 8 digits", "error", "", undefined, 5000);
      return;
    }
    if (!/^\d{6}$/.test(sortCode)) {
      notify("Sort code must be 6 digits", "error", "", undefined, 5000);
      return;
    }
    if (amount <= 0) {
      notify("Please enter a valid amount", "error", "", undefined, 5000);
      return;
    }

    setTransferType("external");
    setPendingFormValues(values);
    setTransferData({
      type: "PPS to External Account Transfer",
      source_account: source.account_no,
      name: values.external_payee_name?.trim(),
      account_number: values.external_account_no,
      sort_code: sortCode,
      amount: amount,
      payment_reference: values.external_payment_reference || "Auto-generated",
    });
    setConfirmOpen(true);
  };

  const handleTransferConfirm = () => {
    if (!transferType || !pendingFormValues) return;

    setTransferring(true);
    setConfirmOpen(false);

    let headers = getConfigPost();
    let data: any = {};
    let endpoint = ENDPOINTS.PPS_MONEY_MANAGEMENT;

    if (transferType === "tillo") {
      data = {
        action: "tillo_float_transfer",
        amount: parseFloat(pendingFormValues.tillo_amount || "0"),
        payment_reference: pendingFormValues.tillo_payment_reference || undefined,
      };
    } else if (transferType === "natwest_rewards") {
      data = {
        action: "natwest_to_rewards_transfer",
        amount: parseFloat(pendingFormValues.natwest_rewards_amount || "0"),
        payment_reference: pendingFormValues.natwest_rewards_payment_reference || undefined,
      };
    } else if (transferType === "external") {
      const source = externalSource(pendingFormValues);
      data = {
        action: "external_transfer",
        source_account_no: source.account_no,
        cardholder_ref: source.cardholder_ref,
        payee_name: pendingFormValues.external_payee_name?.trim(),
        account_no: pendingFormValues.external_account_no,
        sort_code: pendingFormValues.external_sort_code,
        amount: parseFloat(pendingFormValues.external_amount || "0"),
        payment_reference: pendingFormValues.external_payment_reference || undefined,
      };
    }

    axios
      .post(endpoint, data, { headers })
      .then((res: any) => {
        setTransferring(false);
        const responseData = res?.data;
        notify(
          `Transfer successful! Transaction Ref: ${responseData?.txn_ref || "N/A"}`,
          "info",
          "",
          undefined,
          10000
        );
        fetchAllData();
        setPendingFormValues(null);
        setTransferType(null);
        setTransferData(null);
      })
      .catch((error) => {
        setTransferring(false);
        notify(
          error?.response?.data?.message || "Transfer failed",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleTransferCancel = () => {
    setConfirmOpen(false);
    setTransferType(null);
    setTransferData(null);
    setPendingFormValues(null);
  };

  const validate = (values: FormValues) => {
    const errors: any = {};
    return errors;
  };

  return (
    <>
      <Form
        onSubmit={() => {}}
        validate={validate}
        render={({ handleSubmit, values }) => (
          <div>
            <form onSubmit={handleSubmit} noValidate>
              <div className={classes.main}>
                <div className={classes.container}>
                  <Typography variant="h4" className={classes.pageTitle}>
                    PPS Money Management
                  </Typography>

                  <Grid container spacing={3} className={classes.balanceGrid}>
                    <Grid item xs={12} md={6}>
                      <Card className={`${classes.balanceCard} ${classes.rewardsBalanceCard}`}>
                        <CardContent className={classes.balanceCardContent}>
                          <AccountBalanceWalletIcon className={classes.balanceIcon} />
                          <Typography className={classes.balanceLabel}>
                            Rewards Wallet Balance
                          </Typography>
                          {loadingRewards ? (
                            <CircularProgress size={24} style={{ color: "white" }} />
                          ) : rewardsBalance ? (
                            <>
                              <Typography className={classes.balanceValue}>
                                £{rewardsBalance.balance_pounds.toFixed(2)}
                              </Typography>
                              <Typography className={classes.balanceSubtext}>
                                Available for transfer: £{rewardsBalance.available_for_transfer.toFixed(2)}
                              </Typography>
                              <Typography className={classes.balanceSubtext}>
                                Min threshold: £{rewardsBalance.minimum_threshold}
                              </Typography>
                            </>
                          ) : (
                            <Typography className={classes.balanceSubtext}>
                              Unable to load
                            </Typography>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            className={classes.refreshButton}
                            onClick={fetchRewardsBalance}
                            disabled={loadingRewards}
                            startIcon={<RefreshIcon />}
                          >
                            Refresh
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Card className={`${classes.balanceCard} ${classes.configCard}`}>
                        <CardContent className={classes.balanceCardContent}>
                          <SettingsIcon className={classes.balanceIcon} />
                          <Typography className={classes.balanceLabel}>
                            Configuration
                          </Typography>
                          {loadingConfig ? (
                            <CircularProgress size={24} style={{ color: "white" }} />
                          ) : configData ? (
                            <>
                              <Typography className={classes.balanceSubtext}>
                                Rewards Account: {configData.rewards_account_no || "N/A"}
                              </Typography>
                              <Typography className={classes.balanceSubtext}>
                                Rewards Cardholder Ref: {configData.rewards_cardholder_ref || "N/A"}
                              </Typography>
                              <Typography className={classes.balanceSubtext}>
                                Tillo Payee Ref: {configData.tillo_float_payee_reference || "N/A"}
                              </Typography>
                              <Typography className={classes.balanceSubtext}>
                                Rewards Min Threshold: £{configData.rewards_minimum_threshold || "N/A"}
                              </Typography>
                              <Typography className={classes.balanceSubtext}>
                                Tillo Threshold: £{configData.tillo_balance_threshold || "N/A"}
                              </Typography>
                            </>
                          ) : (
                            <Typography className={classes.balanceSubtext}>
                              Unable to load
                            </Typography>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            className={classes.refreshButton}
                            onClick={fetchConfig}
                            disabled={loadingConfig}
                            startIcon={<RefreshIcon />}
                          >
                            Refresh
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Paper className={classes.tabsContainer} elevation={2}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      indicatorColor="primary"
                      textColor="primary"
                      className={classes.tabs}
                      variant="fullWidth"
                    >
                      <Tab 
                        label="Tillo Float Transfer" 
                        icon={<SwapHorizIcon />}
                      />
                      <Tab
                        label="NatWest to Rewards Transfer"
                        icon={<SwapHorizIcon />}
                      />
                      <Tab
                        label="PPS to External Account"
                        icon={<SwapHorizIcon />}
                      />
                      <Tab
                        label="Master Accounts"
                        icon={<AccountBalanceIcon />}
                      />
                    </Tabs>

                    {activeTab === 0 && (
                      <div className={classes.tabPanel}>
                        <Box className={classes.warningBox}>
                          <Typography className={classes.warningText}>
                            This will transfer funds from the Rewards Wallet to Tillo Float. 
                            Available for transfer: £{rewardsBalance?.available_for_transfer?.toFixed(2) || "0.00"} 
                            (after maintaining minimum threshold of £{rewardsBalance?.minimum_threshold || configData?.rewards_minimum_threshold || "N/A"})
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="tillo_amount"
                              component={renderInput}
                              label="Amount (£) *"
                              type="number"
                              className={classes.formField}
                              disabled={transferring}
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="tillo_payment_reference"
                              component={renderInput}
                              label="Payment Reference (optional)"
                              className={classes.formField}
                              disabled={transferring}
                              placeholder="Leave empty for auto-generated"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={transferring || !values.tillo_amount}
                              onClick={() => handleTilloTransferClick(values)}
                              className={classes.button}
                            >
                              {transferring && transferType === "tillo" && (
                                <CircularProgress size={18} thickness={2} style={{ marginRight: "10px", color: "white" }} />
                              )}
                              Transfer to Tillo Float
                            </Button>
                          </Grid>
                        </Grid>
                      </div>
                    )}

                    {activeTab === 1 && (
                      <div className={classes.tabPanel}>
                        <Box className={classes.warningBox}>
                          <Typography className={classes.warningText}>
                            This will send a NatWest FPS transfer directly into the Rewards Wallet,
                            mirroring the automatic top-up flow. Use this for manual top-ups outside
                            the automatic balance alert.
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="natwest_rewards_amount"
                              component={renderInput}
                              label="Amount (£) *"
                              type="number"
                              className={classes.formField}
                              disabled={transferring}
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="natwest_rewards_payment_reference"
                              component={renderInput}
                              label="Payment Reference (optional)"
                              className={classes.formField}
                              disabled={transferring}
                              placeholder="Leave empty for auto-generated"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={transferring || !values.natwest_rewards_amount}
                              onClick={() => handleNatwestRewardsTransferClick(values)}
                              className={classes.button}
                            >
                              {transferring && transferType === "natwest_rewards" && (
                                <CircularProgress size={18} thickness={2} style={{ marginRight: "10px", color: "white" }} />
                              )}
                              Transfer NatWest to Rewards Wallet
                            </Button>
                          </Grid>
                        </Grid>
                      </div>
                    )}

                    {activeTab === 2 && (
                      <div className={classes.tabPanel}>
                        <Box className={classes.warningBox}>
                          <Typography className={classes.warningText}>
                            This creates a new payee on the source PPS account and immediately sends an
                            FPS payment to it. Source fields default to the Rewards Wallet when left empty.
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="external_source_account_no"
                              component={renderInput}
                              label="Source PPS Account No"
                              className={classes.formField}
                              disabled={transferring}
                              placeholder={configData?.rewards_account_no || ""}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="external_cardholder_ref"
                              component={renderInput}
                              label="Source Cardholder Ref"
                              className={classes.formField}
                              disabled={transferring}
                              placeholder={configData?.rewards_cardholder_ref || ""}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Field
                              name="external_payee_name"
                              component={renderInput}
                              label="Payee Name *"
                              className={classes.formField}
                              disabled={transferring}
                              inputProps={{ maxLength: 18 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Field
                              name="external_account_no"
                              component={renderInput}
                              label="Account Number *"
                              className={classes.formField}
                              disabled={transferring}
                              inputProps={{ maxLength: 8, inputMode: "numeric" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Field
                              name="external_sort_code"
                              component={renderInput}
                              label="Sort Code *"
                              className={classes.formField}
                              disabled={transferring}
                              placeholder="12-34-56"
                              inputProps={{ maxLength: 8 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="external_amount"
                              component={renderInput}
                              label="Amount (£) *"
                              type="number"
                              className={classes.formField}
                              disabled={transferring}
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field
                              name="external_payment_reference"
                              component={renderInput}
                              label="Payment Reference (optional)"
                              className={classes.formField}
                              disabled={transferring}
                              placeholder="Leave empty for auto-generated"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={
                                transferring ||
                                !values.external_payee_name ||
                                !values.external_account_no ||
                                !values.external_sort_code ||
                                !values.external_amount
                              }
                              onClick={() => handleExternalTransferClick(values)}
                              className={classes.button}
                            >
                              {transferring && transferType === "external" && (
                                <CircularProgress size={18} thickness={2} style={{ marginRight: "10px", color: "white" }} />
                              )}
                              Create Payee and Transfer
                            </Button>
                          </Grid>
                        </Grid>
                      </div>
                    )}

                    {activeTab === 3 && (
                      <div className={classes.tabPanel}>
                        <PpsMasterAccounts />
                      </div>
                    )}

                  </Paper>


                  <Notification />
                </div>
              </div>
            </form>

            <CustomConfirm
              isOpen={confirmOpen}
              title="Confirm Transfer"
              user={transferData}
              content={ConfirmContent}
              confirm="Confirm Transfer"
              confirmColor="primary"
              ConfirmIcon={Check}
              cancel="Cancel"
              CancelIcon={ErrorOutline}
              onConfirm={handleTransferConfirm}
              onClose={handleTransferCancel}
            />
          </div>
        )}
      />
    </>
  );
};

const ConfirmContent = (props: any) => {
  const data = props.user;
  if (!data) return null;

  return (
    <div style={{ padding: "1.5em" }}>
      <Box
        style={{
          padding: "1.5em",
          backgroundColor: "#f5f7fa",
          borderRadius: "8px",
          marginBottom: "1em",
        }}
      >
        <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
          <strong>Transfer Type:</strong>{" "}
          <span style={{ color: "#667eea" }}>{data.type}</span>
        </Typography>
        {data.source_account && (
          <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
            <strong>Source Account:</strong> {data.source_account}
          </Typography>
        )}
        {data.name && (
          <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
            <strong>Recipient Name:</strong> {data.name}
          </Typography>
        )}
        {data.account_number && (
          <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
            <strong>Account Number:</strong> {data.account_number}
          </Typography>
        )}
        {data.sort_code && (
          <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
            <strong>Sort Code:</strong> {data.sort_code}
          </Typography>
        )}
        <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
          <strong>Amount:</strong>{" "}
          <span style={{ color: "#2e7d32", fontSize: "1.2rem", fontWeight: 600 }}>
            £{data.amount.toFixed(2)}
          </span>
        </Typography>
        {(data.payment_reference || data.reference) && (
          <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
            <strong>Payment Reference:</strong> {data.payment_reference || data.reference}
          </Typography>
        )}
        {data.rewards_balance !== undefined && (
          <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
            <strong>Current Rewards Balance:</strong> £{data.rewards_balance?.toFixed(2)}
          </Typography>
        )}
        {data.available_for_transfer !== undefined && (
          <Typography variant="body1">
            <strong>Available for Transfer:</strong> £{data.available_for_transfer?.toFixed(2)}
          </Typography>
        )}
      </Box>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ textAlign: "center", fontStyle: "italic" }}
      >
        Are you sure you want to proceed with this transfer? This action cannot be undone.
      </Typography>
    </div>
  );
};

export default PpsMoneyManagement;
